// Lib
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Utils
import { getElementId } from '../../../common/elements/utils/elementPropertyUtils';
import {
    getMobileWorkspaceRestoreMode,
    MobileWorkspaceRestoreMode,
} from '../../mobile/utils/mobileWorkspaceRestoreMode';

// Selectors
import { selectIsMobileAppMode } from '../../platform/platformSelector';
import { getCurrentUserQuickNotesRootBoardId } from '../../user/currentUserSelector';

// Actions
import { navigateToBoard, navigateToLogin } from '../../reducers/navigationActions';

// Selectors
import { selectIsSplashVisible } from '../../auth/modals/splash/AppSplash';

// Types
import { ImMNElement } from '../../../common/elements/elementModelTypes';
import { GenericReduxThunkAction } from '../../../common/actions/actionTypes';

const ROOT_PATHNAME = '/';
const isRoot = (pathname: string) => pathname === ROOT_PATHNAME;

/**
 * Determines the board to redirect to when the user is logged in.
 *
 * If they're using the mobile app and the last board they were on was Quick Notes, restore to that board,
 * otherwise restore to the determined "current board".
 */
export const redirectToBoard =
    (currentBoardId: string): GenericReduxThunkAction =>
    (dispatch, getState) => {
        const state = getState();

        const isMobileAppMode = selectIsMobileAppMode(state);

        const shouldRedirectToQuickNotes =
            isMobileAppMode && getMobileWorkspaceRestoreMode() === MobileWorkspaceRestoreMode.QUICK_NOTES;

        if (shouldRedirectToQuickNotes) {
            const quickNotesBoardId = getCurrentUserQuickNotesRootBoardId(state);

            if (quickNotesBoardId) {
                return dispatch(
                    navigateToBoard({ boardId: quickNotesBoardId, trackTiming: false, navigationSource: undefined }),
                );
            }
        }

        if (currentBoardId) {
            return dispatch(
                navigateToBoard({ boardId: currentBoardId, trackTiming: false, navigationSource: undefined }),
            );
        }
    };

interface WorkspaceRootRedirectionManagerProps {
    currentBoard: ImMNElement;
    isUserAvailable: boolean;
    isGuest: boolean;
    location: Location;
}

/**
 * This component is being used to separate the redirection logic out of the WorkspaceInitContainer to account
 * for the scenario that the root URL is loaded (e.g. app.milanote.com) because sub routes are not invoked in
 * this version of React Router when the current route matches exactly ('/').
 */
const WorkspaceRootRedirectionManager = (props: WorkspaceRootRedirectionManagerProps): null => {
    const { location, isUserAvailable, isGuest, currentBoard } = props;

    const dispatch = useDispatch();
    const dispatchRedirectToLogin = useCallback((from: string) => dispatch(navigateToLogin({ from })), [dispatch]);
    const dispatchRedirectToBoard = useCallback(
        (currentBoardId: string) => dispatch(redirectToBoard(currentBoardId)),
        [dispatch],
    );

    const pathname = location?.pathname || '';

    // We need to get the current board ID from the current board because we want to ensure that the
    // current board is available before performing the redirection (so the slug, etc is available).
    const currentBoardId = getElementId(currentBoard);

    const isSplashEnabled = useSelector((state) => selectIsSplashVisible(state, location));

    React.useEffect(() => {
        if (!isRoot(pathname)) return;

        if (isGuest) {
            if (isSplashEnabled) return;

            dispatchRedirectToLogin(pathname);
            return;
        }

        if (isUserAvailable) dispatchRedirectToBoard(currentBoardId);
    }, [pathname, isUserAvailable, currentBoardId, isSplashEnabled]);

    return null;
};

export default WorkspaceRootRedirectionManager;
